import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Box, Spacer, FormField, Textarea, FormHelp, Label, Text, Alert, LoadingButton } from "@zillow/constellation";
import { cookiesToZGUID } from "@zillow/mortgage-urls";
import React, { useRef, useEffect } from "react";
import { useForm, useFormState, FormProvider } from "react-hook-form";
import { useCurrentUser } from "../../hooks/user.esm.js";
import EmailField from "../Fields/EmailField.esm.js";
import NameField from "../Fields/NameField.esm.js";
import TextField from "../Fields/TextField.esm.js";
import AddressAutocompleteField from "./AddressAutocompleteField.esm.js";
import SuccessState from "./SuccessState.esm.js";
import API_ENDPOINTS from "./constants/api.esm.js";
import { triggerFormSubmitClickstreamEvent, triggerPrivacyPolicyClickstreamEvent, triggerSubmitAnotherReportClickstreamEvent, triggerFormFieldEngagementClickstreamEvent } from "./utils/clickstream.esm.js";
import { ifNullStringNull } from "./utils/common.esm.js";
import { useBlockEnvironment } from "../../contexts/BlockEnvironment/BlockEnvironmentProvider.esm.js";
const API_ERROR_MESSAGE = "There was an error submitting the form. Please try again.";
function scrollToTop(ref) {
  setTimeout(() => {
    var _a;
    (_a = ref.current) == null ? void 0 : _a.scrollIntoView({
      behavior: "smooth"
    });
  }, 0);
}
function triggerFailedSubmit(invalidFields = []) {
  triggerFormSubmitClickstreamEvent(false, invalidFields, false);
}
const PropertyReportTransparency = ({
  id
}) => {
  var _a, _b, _c, _d;
  const {
    environment = "prod"
  } = useBlockEnvironment();
  const apiEndpoint = ((_b = (_a = API_ENDPOINTS) == null ? void 0 : _a[environment]) == null ? void 0 : _b.LISTING_CONTACT) || API_ENDPOINTS.prod.LISTING_CONTACT;
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      address: "",
      comments: "",
      mlsName: "",
      userName: "",
      userEmail: ""
    }
  });
  const {
    errors
  } = useFormState({
    control: methods.control
  });
  const {
    data
  } = useCurrentUser(environment === "prod" ? "production" : "qa");
  const user = (data == null ? void 0 : data.user) && typeof data.user === "object" ? data.user : null;
  const [zpid, setZpid] = React.useState("");
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [referenceNumber, setReferenceNumber] = React.useState("");
  const [apiError, setApiError] = React.useState("");
  const containerRef = useRef(null);
  useEffect(() => {
    if (user) {
      const userName = [user.firstName, user.lastName].filter(Boolean).join(" ");
      methods.setValue("userName", userName);
      methods.setValue("userEmail", user.email);
    }
  }, [methods, user]);
  const handleAddressSelect = (address, zpidValue) => {
    methods.setValue("address", address, {
      shouldValidate: true
    });
    if (zpidValue) {
      setZpid(zpidValue);
    }
  };
  const handleBlur = (label) => () => {
    triggerFormFieldEngagementClickstreamEvent(label);
  };
  const handleSubmitAnotherReport = () => {
    triggerSubmitAnotherReportClickstreamEvent("Submit another report", "", "_self");
    setIsSuccess(false);
    setReferenceNumber("");
    methods.reset({
      address: "",
      comments: "",
      mlsName: "",
      userName: methods.getValues("userName"),
      userEmail: methods.getValues("userEmail")
    });
    setZpid("");
    scrollToTop(containerRef);
  };
  const onSubmit = methods.handleSubmit(async (data2) => {
    var _a2;
    setApiError("");
    setIsSubmitting(true);
    const encodedZuid = ifNullStringNull(cookiesToZGUID(document.cookie)) || "";
    const formData = {
      ...data2,
      zpid,
      encodedZuid
    };
    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        const data3 = await response.json();
        if (((_a2 = data3 == null ? void 0 : data3.message) == null ? void 0 : _a2.code) === 200) {
          setReferenceNumber(data3 == null ? void 0 : data3.key);
          triggerFormSubmitClickstreamEvent(true, [], true, "");
          setIsSuccess(true);
          setApiError("");
          scrollToTop(containerRef);
        } else {
          setApiError(API_ERROR_MESSAGE);
          triggerFailedSubmit();
        }
      } else {
        setApiError(API_ERROR_MESSAGE);
        triggerFailedSubmit();
      }
    } catch (e) {
      setApiError(API_ERROR_MESSAGE);
      triggerFailedSubmit();
    }
    setIsSubmitting(false);
  }, (errors2) => {
    const invalidFields = Object.keys(errors2);
    triggerFailedSubmit(invalidFields);
    setIsSubmitting(false);
  });
  if (isSuccess) {
    return /* @__PURE__ */ jsx(SuccessState, { referenceNumber, onSubmitAnother: handleSubmitAnotherReport });
  }
  return /* @__PURE__ */ jsx(FormProvider, { ...methods, children: /* @__PURE__ */ jsxs(Box, { id, ref: containerRef, children: [
    /* @__PURE__ */ jsx(AddressAutocompleteField, { onSelect: handleAddressSelect, required: true, label: "Property address", error: (_c = errors == null ? void 0 : errors.address) == null ? void 0 : _c.message, placeholder: "Enter complete property address with street, city, state, and ZIP code", description: "Enter the complete property address for the listing with the violation.", onBlur: handleBlur("Property address") }),
    /* @__PURE__ */ jsx(Spacer, { marginBottom: "xs" }),
    /* @__PURE__ */ jsxs(Box, { as: "form", onSubmit, children: [
      /* @__PURE__ */ jsx("input", { type: "hidden", ...methods.register("address", {
        required: {
          value: true,
          message: "Property address is required"
        }
      }) }),
      /* @__PURE__ */ jsx(FormField, { label: /* @__PURE__ */ jsxs(Label, { htmlFor: "comments", children: [
        "Report details",
        " ",
        /* @__PURE__ */ jsx(Text, { fontColor: "textLight", fontType: "bodySmall", children: "(optional)" })
      ] }), description: /* @__PURE__ */ jsx(FormHelp, { error: !!errors.comments, children: ((_d = errors.comments) == null ? void 0 : _d.message) || "Include any details about the listing or the violation that would be helpful." }), control: /* @__PURE__ */ jsx(Textarea, { id: "comments", placeholder: "Any information that might help our investigation, including a web address or URL if relevant.", rows: "4", ...methods.register("comments", {
        onBlur: handleBlur("Report details")
      }) }) }),
      /* @__PURE__ */ jsx(Spacer, { marginBottom: "xs" }),
      /* @__PURE__ */ jsx(NameField, { name: "userName", label: "Your name", required: true, placeholder: "Enter your full name", onBlur: handleBlur("Your name") }),
      /* @__PURE__ */ jsx(EmailField, { name: "userEmail", label: "Your email", required: true, placeholder: "your.email@example.com", onBlur: handleBlur("Your email") }),
      /* @__PURE__ */ jsx(TextField, { name: "mlsName", label: /* @__PURE__ */ jsxs(Fragment, { children: [
        "MLS name",
        " ",
        /* @__PURE__ */ jsx(Text, { fontColor: "textLight", fontType: "bodySmall", children: "(optional)" })
      ] }), placeholder: "Enter MLS name", onBlur: handleBlur("MLS name") }),
      /* @__PURE__ */ jsxs(Text, { as: "p", fontType: "legal", children: [
        "We respect your privacy—your personal information will only be used to follow up on this report and will not be sold or shared with third parties. The information you provide will help us enforce Zillow's listing access standards. By submitting your contact information, you agree that Zillow Group may contact you to follow up or verify the details you provided. Submitting this form does not create a contractual relationship or guarantee a response. For more information, please review our",
        " ",
        /* @__PURE__ */ jsx("a", { href: "https://www.zillow.com/privacy/", target: "_blank", rel: "noopener noreferrer", onClick: () => triggerPrivacyPolicyClickstreamEvent("Privacy Policy", "https://www.zillow.com/privacy/", "_blank"), children: "Privacy Policy" }),
        "."
      ] }),
      /* @__PURE__ */ jsx(Spacer, { marginBottom: "md" }),
      apiError && /* @__PURE__ */ jsx(Box, { marginBottom: "sm", children: /* @__PURE__ */ jsx(Alert, { appearance: "error", body: apiError }) }),
      /* @__PURE__ */ jsx(LoadingButton, { type: "submit", buttonType: "primary", loading: isSubmitting, disabled: isSubmitting, children: "Submit report" })
    ] })
  ] }) });
};
export {
  PropertyReportTransparency as default
};
